/**
 * Detail block for a compliance requirement's AI position assessment.
 *
 * Rendered inside `RequirementDrawer`, whose fixed-width column already bounds
 * the measure — so no `max-w` here — and which owns the padding, so this
 * component must not add outer margin.
 */

import { Building2, Check, FileText, Sparkles } from 'lucide-react'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { displayPositionLabel } from '@/components/extraction/PositionPill'
import { useExtractionStore } from '@/stores/extraction-store'
import { usePreviewStore } from '@/stores/preview-store'
import { displayCitationPage, toPrecisePreviewHighlight } from '@/lib/citation-preview'
import { displayStateOf } from '@/lib/compliance-position'
import type {
  CriticalityValue,
  ExtractedComplianceItem,
  PositionCitation,
  PositionConfidence,
} from '@/types/extraction'

// ===================
// Constants
// ===================

const CRITICALITY_OPTIONS: Array<{ value: CriticalityValue; label: string }> = [
  { value: 'mandatory', label: 'Mandatory' },
  { value: 'desirable', label: 'Desirable' },
  { value: 'information', label: 'Information' },
]

const CONFIDENCE_VARIANT: Record<PositionConfidence, BadgeProps['variant']> = {
  high: 'success',
  medium: 'warning',
  low: 'outline',
}

/**
 * The assessor is asked to close a rationale with a concrete next step. Pull it
 * out so the action reads as an instruction rather than the tail of a sentence.
 *
 * The arrow is part of the marker, not part of the rationale: `applyAssessment`
 * writes the hint as `\n→ Action: …`, so leaving the `→` out of the match
 * strands it on the end of the body (both here and in the Excel export).
 *
 * It is a STORAGE marker, not a label — nothing renders it. The UI and the
 * export both name this field "Recommended action"; printing the `→ Action:`
 * the parser just stripped would be leaking the wire format onto the screen.
 */
const ACTION_MARKER = /(?:→\s*)?\bAction:\s*/i

/**
 * Shared tail of every manual-position note. Named the same way in all three
 * states — an earlier draft said "it no longer updates this position" in one
 * branch and "AI" in the others, which read as two different subjects.
 */
const MANUAL_NOTE_SUFFIX =
  'AI no longer updates this position (set it to Unassessed to hand it back)'

export function splitRationaleAction(rationale: string | null | undefined): {
  body: string
  action: string | null
} {
  if (!rationale) return { body: '', action: null }
  const match = ACTION_MARKER.exec(rationale)
  if (!match) return { body: rationale.trim(), action: null }
  const body = rationale.slice(0, match.index).trim()
  const action = rationale.slice(match.index + match[0].length).trim()
  return { body, action: action || null }
}

// ===================
// Types
// ===================

export interface AssessmentBlockProps {
  bidId: string
  item: ExtractedComplianceItem
  readOnly?: boolean
}

// ===================
// Component
// ===================

export function AssessmentBlock({ bidId, item, readOnly }: AssessmentBlockProps) {
  const confirmPositions = useExtractionStore((s) => s.confirmPositions)
  const startAssessment = useExtractionStore((s) => s.startAssessment)
  const updateItemCriticality = useExtractionStore((s) => s.updateItemCriticality)
  const isAssessing = useExtractionStore((s) => s.assessmentRun.status === 'running')
  const openPreview = usePreviewStore((s) => s.openPreview)

  const itemId = item.id ?? null
  const isManual = item.positionSource === 'manual'
  // Nothing to attest to: the AI never searched for this requirement. The
  // person answers it by choosing a position, which clears the label.
  const needsConfirmation = displayStateOf(item) === 'needs_confirmation'
  const { body, action } = splitRationaleAction(item.positionRationale)
  const citations = item.positionCitations ?? []
  // Keyed on what there is to SHOW, not on `positionAssessedAt` — the pending
  // spec decision may null the AI fields on a manual override, and a partial
  // null would otherwise render a heading with nothing under it.
  const hasAiDetail =
    body.length > 0 ||
    action !== null ||
    item.positionConfidence != null ||
    citations.length > 0
  const wasAssessed = item.positionAssessedAt != null
  const canWrite = !readOnly && !!itemId

  /**
   * What a manual position actually means (mig 156).
   *
   * `positionSource: 'manual'` is stamped by BOTH Confirm and an override, so
   * the two used to be indistinguishable after a reload and the copy had to
   * hedge. `aiPosition` is the AI's own last verdict, frozen at the moment the
   * row went manual, so the distinction is now derivable: same value → the user
   * agreed, different value → they overrode it. `null` (never assessed, or a row
   * last assessed before mig 156) supports neither claim, so that case keeps the
   * old neutral wording rather than guessing.
   */
  const aiPosition = item.aiPosition ?? null
  const canCompare = aiPosition !== null && item.position != null
  const agreedWithAi = canCompare && item.position === aiPosition
  const overrodeAi = canCompare && item.position !== aiPosition

  // The AI's verdict is named with the SAME label the pill uses, so a downgraded
  // 'unassessed' reads as "Needs review" (or "Needs your confirmation" for a
  // requirement it never searched for) here too, rather than as two surfaces
  // disagreeing about what the assessor said. The reason is frozen with the
  // AI's verdict, so it still describes `aiPosition` after an override.
  //
  // No second person: nothing records WHO set the position, and in a shared org
  // the reader is usually not that person — so the copy describes what happened
  // to the position, never who did it.
  const manualNote = !isManual
    ? null
    : agreedWithAi
      ? `confirmed as the AI assessed it — ${MANUAL_NOTE_SUFFIX}`
      : overrodeAi
        ? `changed from the AI’s ${displayPositionLabel(
            aiPosition,
            item.positionAssessedAt ?? null,
            item.unassessedReason ?? null
          )} — ${MANUAL_NOTE_SUFFIX}`
        : `set manually — ${MANUAL_NOTE_SUFFIX}`

  const handleCitationClick = (citation: Extract<PositionCitation, { source: 'library' }>) => {
    const precise = toPrecisePreviewHighlight(
      citation.highlightData,
      citation.quote,
      `position-${citation.documentId}-${citation.chunkIndex}`
    )
    void openPreview({
      documentId: citation.documentId,
      documentName: citation.documentName,
      // 'analysis' docks the reader beside the Analysis content on wide
      // screens instead of covering it with the full-screen modal.
      context: 'analysis',
      ...(citation.page != null ? { page: citation.page } : {}),
      ...(precise ?? {}),
    })
  }

  return (
    // No outer margin and no measure cap — the drawer column bounds the line
    // length and owns the padding.
    //
    // text-sm, NOT text-xs: a blanket text-xs here dragged the rationale a size
    // below the requirement description sitting directly above it. Labels,
    // chips and badges below opt back down to text-xs individually.
    <div className="space-y-2 text-sm">
      {hasAiDetail ? (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">
              {isManual ? 'Last AI assessment' : 'AI assessment'}
            </span>
            {item.positionConfidence && (
              <Badge
                variant={CONFIDENCE_VARIANT[item.positionConfidence]}
                dot={false}
                className="text-xs px-1.5 py-0 font-normal"
              >
                {item.positionConfidence} confidence
              </Badge>
            )}
            {manualNote && <span className="text-meta">{manualNote}</span>}
          </div>
          {body && <p className="text-muted-foreground">{body}</p>}
          {/* On an AI-owned row the action is a live instruction and keeps its
              prominence — but through the LABEL's weight and colour, not by
              darkening the whole line. A fully `text-foreground` line made the
              model's suggestion the darkest prose in the drawer, above both the
              rationale it came from and the tender's own requirement at the top,
              which overclaims on a low-confidence assessment. This is the same
              shape as the "Evidence required:" line four lines below.

              Once the position is manual the action belongs to a superseded
              verdict, so even the label drops to muted and it is re-labelled as
              the AI's suggestion — a "Comply" row must not carry what looks like
              an outstanding task. The text is kept either way: a reviewer
              judging the override needs it. */}
          {action &&
            (isManual ? (
              <p className="text-muted-foreground">
                <span className="font-medium">AI’s recommended action:</span> {action}
              </p>
            ) : (
              <p className="text-muted-foreground">
                <span className="font-medium text-foreground">Recommended action:</span>{' '}
                {action}
              </p>
            ))}
        </div>
      ) : wasAssessed ? (
        <p className="text-muted-foreground">
          Assessed — no reasoning was recorded for this requirement.
        </p>
      ) : (
        <p className="text-muted-foreground">
          Not assessed yet — run an assessment to get a position and the evidence behind it.
        </p>
      )}

      {item.evidence && (
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">Evidence required:</span>{' '}
          {item.evidence}
        </p>
      )}

      {citations.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {citations.map((citation, index) =>
            citation.source === 'library' ? (
              <button
                key={`library-${index}`}
                type="button"
                onClick={() => handleCitationClick(citation)}
                title={citation.quote}
                className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <FileText className="h-3 w-3" />
                {citation.documentName}
                {displayCitationPage(citation.page) != null
                  ? ` · p.${displayCitationPage(citation.page)}`
                  : ''}
              </button>
            ) : (
              <a
                key={`profile-${index}`}
                href="/app/settings?tab=profile"
                title={citation.profileField}
                className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Building2 className="h-3 w-3" />
                {citation.detail || citation.profileField}
              </a>
            )
          )}
        </div>
      )}

      {/* One left-aligned cluster: the actions sit beside the criticality control
          rather than at the far edge, where they read as arbitrarily placed. */}
      <div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Criticality</span>
          <Select
            value={item.criticality ?? 'desirable'}
            onValueChange={(next) => {
              if (itemId) void updateItemCriticality(bidId, itemId, next as CriticalityValue)
            }}
            disabled={!canWrite}
          >
            <SelectTrigger aria-label="Criticality" className="h-7 w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CRITICALITY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {canWrite && (
          <div className="flex items-center gap-1">
            {!isManual && !needsConfirmation && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => void confirmPositions(bidId, [itemId])}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Confirm
              </Button>
            )}
            {/* Hidden on a manual row, not just disabled: the engine's own
                item selection drops `positionSource: 'manual'` rows, so the
                button would spin up a run that provably cannot touch this
                requirement. The badge above already says AI no longer updates
                it, and setting the pill back to Unassessed is the way back. */}
            {!isManual && (
              <Button
                variant="ai-ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={isAssessing}
                title={isAssessing ? 'An assessment is already running' : undefined}
                onClick={() => void startAssessment(bidId, [itemId])}
              >
                <Sparkles className="mr-1 h-3.5 w-3.5" />
                Reassess
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
